import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
	AlertTriangle,
	ArrowRight,
	CalendarDays,
	CheckCircle2,
	Circle,
	MessageSquare,
	Plus,
} from "lucide-react";
import type React from "react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import * as actionService from "@/api/services/actionService";
import { InfoTooltip } from "@/components/tours/InfoTooltip";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { ChatDiscussion } from "@/components-shared/ChatDiscussion";
import { useActions } from "@/contexts/ActionsContext";
import { useApp } from "@/contexts/AppContext";
import { DEMO_USERS } from "@/contexts/UsersContext";
import { useTerminology } from "@/hooks/useTerminology";
import { cn } from "@/lib/utils";
import type { Action, ActionComment } from "@/types/actions";

interface ActionsCardProps {
	onNavigate?: (page: string) => void;
}

const personaName = (id: string) => {
	const u = DEMO_USERS.find((u) => u.id === id);
	return u ? u.name : id;
};

const formatDate = (d: Date) =>
	d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });

const isOverdue = (action: Action) =>
	action.status === "open" && new Date() > action.dueDate;

// ── Mini Discussion Modal ─────────────────────────────────────────────────────
interface MiniDiscussionModalProps {
	action: Action | null;
	currentUserId: string;
	isLiveMode: boolean;
	onPostComment: (actionId: string, text: string) => void;
	onClose: () => void;
}

const MiniDiscussionModal: React.FC<MiniDiscussionModalProps> = ({
	action,
	currentUserId,
	isLiveMode,
	onPostComment,
	onClose,
}) => {
	const [isSending, setIsSending] = useState(false);
	const queryClient = useQueryClient();

	const numericActionId = action ? Number(action.id) : 0;

	const { data: liveMessagesData, isLoading: isLoadingMessages } = useQuery({
		queryKey: ["actionMessages", numericActionId],
		queryFn: () =>
			actionService.getActionMessageViewList({
				actionId: { eq: numericActionId },
			}),
		enabled: isLiveMode && !!action,
		staleTime: 10_000,
	});

	const liveComments: ActionComment[] = useMemo(() => {
		if (!liveMessagesData?.edges) return [];
		return liveMessagesData.edges.map((edge) => ({
			id: String(edge.node.id),
			actionId: String(edge.node.actionId),
			authorId: edge.node.authorId,
			authorName: edge.node.authorName ?? edge.node.authorId,
			authorRole: "",
			text: edge.node.message,
			createdAt: new Date(edge.node.dateCreated),
		}));
	}, [liveMessagesData]);

	const comments = isLiveMode ? liveComments : (action?.comments ?? []);

	if (!action) return null;

	const handleSend = async (text: string) => {
		if (isLiveMode) {
			setIsSending(true);
			try {
				await actionService.addActionMessage(numericActionId, {
					message: text,
				});
				queryClient.invalidateQueries({
					queryKey: ["actionMessages", numericActionId],
				});
				queryClient.invalidateQueries({ queryKey: ["actions"] });
			} finally {
				setIsSending(false);
			}
		} else {
			onPostComment(action.id, text);
		}
	};

	return (
		<Dialog open={!!action} onOpenChange={onClose}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2 text-base">
						<MessageSquare className="h-4 w-4 text-primary" />
						Discussion
					</DialogTitle>
					<p className="text-xs text-muted-foreground truncate">
						{action.title}
					</p>
				</DialogHeader>

				<ChatDiscussion
					messages={comments}
					currentUserId={currentUserId}
					onSend={handleSend}
					isSending={isSending}
					isLoading={isLiveMode && isLoadingMessages}
					emptyStateTitle="No messages yet"
					emptyStateDescription="Start the conversation."
					placeholder={"Type a message\u2026 (Enter to send)"}
					height="h-72"
					size="sm"
				/>
			</DialogContent>
		</Dialog>
	);
};

// ── Mini Complete Modal ───────────────────────────────────────────────────────
interface MiniCompleteModalProps {
	action: Action | null;
	onConfirm: (comment: string) => void;
	onClose: () => void;
}

const MiniCompleteModal: React.FC<MiniCompleteModalProps> = ({
	action,
	onConfirm,
	onClose,
}) => {
	const [comment, setComment] = useState("");
	if (!action) return null;

	return (
		<Dialog open={!!action} onOpenChange={onClose}>
			<DialogContent className="sm:max-w-sm">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2 text-base">
						<CheckCircle2 className="h-4 w-4 text-teal-700" />
						Mark as Complete
					</DialogTitle>
				</DialogHeader>
				<div className="space-y-3 py-1">
					<p className="text-sm font-medium">{action.title}</p>
					<div className="space-y-1">
						<Label htmlFor="card-complete-comment" className="text-xs">
							Comment <span className="text-destructive">*</span>
						</Label>
						<Textarea
							id="card-complete-comment"
							placeholder="What was done or the outcome…"
							value={comment}
							onChange={(e) => setComment(e.target.value)}
							rows={2}
							className="resize-none text-sm"
						/>
					</div>
				</div>
				<DialogFooter>
					<Button variant="outline" size="sm" onClick={onClose}>
						Cancel
					</Button>
					<Button
						size="sm"
						onClick={() => {
							if (comment.trim()) onConfirm(comment.trim());
						}}
						disabled={!comment.trim()}
						className="bg-teal-600 hover:bg-teal-700 text-white"
					>
						Done
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

// ── Card ─────────────────────────────────────────────────────────────────────

export default function ActionsCard({ onNavigate }: ActionsCardProps) {
	const { mode } = useApp();
	const { getActionsTerm } = useTerminology();
	const { actions, effectiveUserId, completeAction, addActionComment } =
		useActions();
	const [completeTarget, setCompleteTarget] = useState<Action | null>(null);
	const [discussTarget, setDiscussTarget] = useState<Action | null>(null);

	// Keep discussion modal in sync with latest action state
	const discussAction = discussTarget
		? (actions.find((a) => a.id === discussTarget.id) ?? null)
		: null;

	const assignedToMe = actions
		.filter((a) => a.assignedTo === effectiveUserId && a.status === "open")
		.sort((a, b) => a.dueDate.getTime() - b.dueDate.getTime());

	const assignedByMe = actions
		.filter(
			(a) =>
				a.assignedBy === effectiveUserId &&
				a.assignedTo !== effectiveUserId &&
				a.status === "open",
		)
		.sort((a, b) => a.dueDate.getTime() - b.dueDate.getTime());

	const myOpenCount = actions.filter(
		(a) => a.assignedTo === effectiveUserId && a.status === "open",
	).length;
	const delegatedCount = actions.filter(
		(a) =>
			a.assignedBy === effectiveUserId &&
			a.assignedTo !== effectiveUserId &&
			a.status === "open",
	).length;

	const handleCompleteConfirm = (comment: string) => {
		if (!completeTarget) return;
		completeAction({ actionId: completeTarget.id, completionComment: comment });
		toast.success(`${getActionsTerm(true)} marked as complete`);
		setCompleteTarget(null);
	};

	const ActionRow: React.FC<{ action: Action; showAssignee?: boolean }> = ({
		action,
		showAssignee,
	}) => {
		const overdue = isOverdue(action);
		const commentCount = action.messageCount ?? action.comments?.length ?? 0;
		return (
			<div
				className={cn(
					"flex items-center gap-2 p-2.5 border rounded-md",
					overdue && "border-destructive/40 bg-destructive/5",
				)}
			>
				<button
					type="button"
					onClick={() => setCompleteTarget(action)}
					className="flex-shrink-0 text-muted-foreground hover:text-teal-700 transition-colors"
					title="Mark as complete"
				>
					<Circle className="h-4 w-4" />
				</button>
				<div className="flex-1 min-w-0">
					<p className="text-xs font-medium truncate">{action.title}</p>
					<div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
						<span className="flex items-center gap-0.5">
							<CalendarDays className="h-3 w-3" />
							{formatDate(action.dueDate)}
						</span>
						{showAssignee && (
							<span className="truncate">{personaName(action.assignedTo)}</span>
						)}
					</div>
				</div>
				{overdue && (
					<AlertTriangle className="h-3.5 w-3.5 text-destructive flex-shrink-0" />
				)}
				<button
					type="button"
					onClick={() => setDiscussTarget(action)}
					className="relative flex-shrink-0 text-muted-foreground hover:text-primary transition-colors"
					title="Discussion"
				>
					<MessageSquare className="h-3.5 w-3.5" />
					{commentCount > 0 && (
						<span className="absolute -top-1.5 -right-1.5 h-3.5 w-3.5 rounded-full bg-primary text-primary-foreground text-[9px] flex items-center justify-center font-medium leading-none">
							{commentCount}
						</span>
					)}
				</button>
			</div>
		);
	};

	return (
		<>
			<Card className="h-full flex flex-col">
				<CardHeader className="flex flex-row items-center justify-between border-b">
					<div className="flex items-center gap-1">
						<span className="text-lg font-semibold text-neutral-900">
							{getActionsTerm(true)}
						</span>
						<InfoTooltip
							title={getActionsTerm(true)}
							content={`Tasks assigned to you and ${getActionsTerm(false)} you have delegated. Tick any item to mark it as complete — the person who assigned it will be notified.`}
						/>
					</div>
					<Button
						variant="ghost"
						size="sm"
						className="h-7 w-7 p-0"
						onClick={() => onNavigate?.("actions")}
						title={`Create ${getActionsTerm(false)}`}
					>
						<Plus className="h-4 w-4" />
					</Button>
				</CardHeader>
				<CardContent className="p-0 flex flex-col flex-1 min-h-0">
					<div className="px-6 pt-4 flex flex-col flex-1 min-h-0">
						<Tabs
							defaultValue="assigned-to-me"
							className="flex flex-col flex-1 min-h-0"
						>
							<TabsList className="w-full h-8 flex-shrink-0">
								<TabsTrigger
									value="assigned-to-me"
									className="flex-1 text-xs h-7 flex items-center gap-1.5"
								>
									Assigned to Me
									{myOpenCount > 0 && (
										<Badge
											variant="secondary"
											className="h-4 px-1 text-xs leading-none"
										>
											{myOpenCount}
										</Badge>
									)}
								</TabsTrigger>
								<TabsTrigger
									value="assigned-by-me"
									className="flex-1 text-xs h-7 flex items-center gap-1.5"
								>
									Delegated
									{delegatedCount > 0 && (
										<Badge
											variant="secondary"
											className="h-4 px-1 text-xs leading-none"
										>
											{delegatedCount}
										</Badge>
									)}
								</TabsTrigger>
							</TabsList>

							<TabsContent
								value="assigned-to-me"
								className="mt-2 flex-1 min-h-0"
							>
								<div className="overflow-y-auto max-h-[260px] space-y-1.5 pr-1 pb-4">
									{assignedToMe.length === 0 ? (
										<p className="text-xs text-muted-foreground text-center py-4">
											No open actions assigned to you.
										</p>
									) : (
										assignedToMe.map((a) => <ActionRow key={a.id} action={a} />)
									)}
								</div>
							</TabsContent>

							<TabsContent
								value="assigned-by-me"
								className="mt-2 flex-1 min-h-0"
							>
								<div className="overflow-y-auto max-h-[260px] space-y-1.5 pr-1 pb-4">
									{assignedByMe.length === 0 ? (
										<p className="text-xs text-muted-foreground text-center py-4">
											No open actions delegated to others.
										</p>
									) : (
										assignedByMe.map((a) => (
											<ActionRow key={a.id} action={a} showAssignee />
										))
									)}
								</div>
							</TabsContent>
						</Tabs>
					</div>

					<div className="px-6 py-3 border-t flex justify-end mt-auto">
						<Button
							variant="ghost"
							size="sm"
							className="text-neutral-900 hover:bg-teal-50 hover:text-teal-700"
							onClick={() => onNavigate?.("actions")}
						>
							View all actions
							<ArrowRight className="h-3 w-3 ml-1" />
						</Button>
					</div>
				</CardContent>
			</Card>

			<MiniCompleteModal
				action={completeTarget}
				onConfirm={handleCompleteConfirm}
				onClose={() => setCompleteTarget(null)}
			/>
			<MiniDiscussionModal
				action={discussAction}
				currentUserId={effectiveUserId}
				isLiveMode={mode !== "demo"}
				onPostComment={addActionComment}
				onClose={() => setDiscussTarget(null)}
			/>
		</>
	);
}
